import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './StatementGeneration.css';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import EmailService from './EmailService';

function StatementGeneration({ onClose }) {
  const [schools, setSchools] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Form state
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [filterSchool, setFilterSchool] = useState('');

  // Radio button states for each section
  const [statementTypeSingle, setStatementTypeSingle] = useState('current');
  const [statementTypeSchool, setStatementTypeSchool] = useState('current');
  const [statementTypeAll, setStatementTypeAll] = useState('current');

  // Toggle: include a "Payments Received" section with payment dates (off by default)
  const [includePaymentDatesSingle, setIncludePaymentDatesSingle] = useState(false);

  // Custom message state - persisted to franchisee doc
  const [customMessage, setCustomMessage] = useState('');
  const [statementNote, setStatementNote] = useState('');
  const [customMessageLoaded, setCustomMessageLoaded] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Statement settings from Franchisee Information (Setup)
  const [statementSettings, setStatementSettings] = useState({
    statementTitle: '',
    statementSubtitle: '',
    showFranchiseeNameOnStatement: true,
    showFranchiseeEmailOnStatement: false,
    showFranchiseePhoneOnStatement: true,
  });

  // Bulk email state
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentStudent: '' });
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showDeliveryReport, setShowDeliveryReport] = useState(false);
  const [deliveryReport, setDeliveryReport] = useState(null);

  // ── Invoice Per School ──────────────────────────────────────
  const [invoiceTerm, setInvoiceTerm] = useState(1);
  const [invoiceSchool, setInvoiceSchool] = useState('');
  const [invoiceStats, setInvoiceStats] = useState({ owing: 0, paidUp: 0 });
  const [invoiceStatsLoading, setInvoiceStatsLoading] = useState(false);
  const [invoiceSchoolStudents, setInvoiceSchoolStudents] = useState([]);
  const [invoiceSchoolLoading, setInvoiceSchoolLoading] = useState(false);
  const [invoiceEmailLogs, setInvoiceEmailLogs] = useState({});
  const [isZipping, setIsZipping] = useState(false);
  const [isBulkInvoicing, setIsBulkInvoicing] = useState(false);
  const [invoiceProgress, setInvoiceProgress] = useState({ current: 0, total: 0, currentStudent: '' });
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [showInvoiceReport, setShowInvoiceReport] = useState(false);
  const [invoiceReport, setInvoiceReport] = useState(null);

  // ============================================================
  // DATA LOADING
  // ============================================================

  useEffect(() => {
    loadSchools();
    loadCustomMessage();
  }, []);

  useEffect(() => {
    if (selectedSchool || filterSchool) {
      loadStudents(selectedSchool || filterSchool);
    }
  }, [selectedSchool, filterSchool]);

  // Invoice Per School - load logs and stats on mount
  useEffect(() => {
    loadInvoiceEmailLogs();
    loadInvoiceStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload stats when term changes
  useEffect(() => {
    loadInvoiceStats();
  }, [invoiceTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load school students when school selected
  useEffect(() => {
    if (invoiceSchool) {
      loadInvoiceSchoolStudents(invoiceSchool);
    } else {
      setInvoiceSchoolStudents([]);
    }
  }, [invoiceSchool]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSchools = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const schoolsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSchools(schoolsList);
    } catch (error) {
      console.error('Error loading schools:', error);
      alert('Error loading schools');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async (schoolId) => {
    if (!schoolId) {
      setStudents([]);
      return;
    }

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const schoolRef = doc(db, 'schools', schoolId);
      
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('schoolRef', '==', schoolRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const studentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      studentsList.sort((a, b) => a.studentName.localeCompare(b.studentName));
      setStudents(studentsList);
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Error loading students');
    }
  };

  // ============================================================
  // CUSTOM MESSAGE - Load / Save to Franchisee doc
  // ============================================================

  const loadCustomMessage = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const franchiseeDoc = await getDoc(franchiseeRef);
      
      if (franchiseeDoc.exists()) {
        const data = franchiseeDoc.data();
        setCustomMessage(data.invoiceCustomMessage || '');
        setStatementNote(data.invoiceStatementNote || '');
        setStatementSettings({
          statementTitle: data.statementTitle || '',
          statementSubtitle: data.statementSubtitle || '',
          showFranchiseeNameOnStatement: data.showFranchiseeNameOnStatement !== undefined ? data.showFranchiseeNameOnStatement : true,
          showFranchiseeEmailOnStatement: data.showFranchiseeEmailOnStatement || false,
          showFranchiseePhoneOnStatement: data.showFranchiseePhoneOnStatement !== undefined ? data.showFranchiseePhoneOnStatement : true,
        });
      }
      setCustomMessageLoaded(true);
    } catch (error) {
      console.error('Error loading custom message:', error);
      setCustomMessageLoaded(true);
    }
  };

  const saveCustomMessage = async (message) => {
    setSavingMessage(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      await updateDoc(franchiseeRef, {
        invoiceCustomMessage: message
      });
    } catch (error) {
      console.error('Error saving custom message:', error);
    } finally {
      setSavingMessage(false);
    }
  };

  const saveStatementNote = async (note) => {
    setSavingNote(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      await updateDoc(franchiseeRef, {
        invoiceStatementNote: note
      });
    } catch (error) {
      console.error('Error saving statement note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  // Auto-save custom message after user stops typing (debounce)
  useEffect(() => {
    if (!customMessageLoaded) return;
    const timer = setTimeout(() => {
      saveCustomMessage(customMessage);
    }, 1000);
    return () => clearTimeout(timer);
  }, [customMessage, customMessageLoaded]);

  // Auto-save statement note after user stops typing (debounce)
  useEffect(() => {
    if (!customMessageLoaded) return;
    const timer = setTimeout(() => {
      saveStatementNote(statementNote);
    }, 1000);
    return () => clearTimeout(timer);
  }, [statementNote, customMessageLoaded]);

  // ============================================================
  // TERM CALCULATIONS
  // ============================================================

  const determineCurrentAndNextTerm = (termStructure) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentYear = today.getFullYear();
    
    const termDates = termStructure.termDates || {};
    const termNumbers = Object.keys(termDates)
      .filter(key => key.startsWith('term'))
      .map(key => parseInt(key.replace('term', '')))
      .sort((a, b) => a - b);
    
    const maxTerm = Math.max(...termNumbers);
    let currentTerm = null;
    let nextTerm = null;
    
    for (const termNum of termNumbers) {
      const termData = termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;

      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);

      if (today >= termStart && today <= termEnd) {
        currentTerm = termNum;
        if (termNum < maxTerm) nextTerm = termNum + 1;
        break;
      }
      
      if (today < termStart && currentTerm === null) {
        currentTerm = termNum;
        if (termNum < maxTerm) nextTerm = termNum + 1;
        break;
      }
    }
    
    if (currentTerm === null) {
      currentTerm = maxTerm;
      nextTerm = null;
    }
    
    return { currentTerm, nextTerm, maxTerm };
  };

  const generateInvoiceData = (student, termStructure, statementType = 'current') => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentYear = today.getFullYear();
    
    const { currentTerm, nextTerm, maxTerm } = determineCurrentAndNextTerm(termStructure);
    
    const studentStartDate = student.startDate?.toDate 
      ? student.startDate.toDate() 
      : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);
    
    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];
    
    let maxTermToShow = 0;
    
    if (statementType === 'allTerms') {
      for (let termNum = 1; termNum <= maxTerm; termNum++) {
        const termData = termStructure.termDates[`term${termNum}`];
        if (!termData || !termData.start || !termData.end) continue;
        const [startMonth, startDay] = termData.start.split('-').map(Number);
        const [endMonth, endDay] = termData.end.split('-').map(Number);
        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        termEnd.setHours(23, 59, 59, 999);
        if (studentStartDate <= termEnd) maxTermToShow = termNum;
      }
    } else {
      for (let termNum = 1; termNum <= maxTerm; termNum++) {
        const termData = termStructure.termDates[`term${termNum}`];
        if (!termData || !termData.start || !termData.end) continue;
        const [startMonth, startDay] = termData.start.split('-').map(Number);
        const [endMonth, endDay] = termData.end.split('-').map(Number);
        const termStart = new Date(currentYear, startMonth - 1, startDay);
        termStart.setHours(0, 0, 0, 0);
        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        termEnd.setHours(23, 59, 59, 999);

        const shouldCharge = (
          studentStartDate <= termEnd &&
          today >= termStart &&
          (student.isActive || 
            (student.deactivationInfo?.deactivatedDate && 
              new Date(student.deactivationInfo.deactivatedDate) > termStart))
        );
        if (shouldCharge) maxTermToShow = termNum;
      }
      if (statementType === 'includeNext' && maxTermToShow < maxTerm) {
        maxTermToShow = maxTermToShow + 1;
      }
    }
    
    const adminOverride = feeOverrides.find(o => o.overrideType === 'admin' || o.overrideType === 'both');
    const adminDue = adminOverride && adminOverride.adminFee !== undefined 
      ? adminOverride.adminFee : (student.adminFee || 0);
    const adminPaid = student.termPayments?.admin || 0;
    
    let totalDue = adminDue;
    let totalPaid = adminPaid;
    
    const terms = [];
    for (let termNum = 1; termNum <= maxTermToShow; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;
      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);
      const isFutureTerm = today < termStart;
      const isNextTerm = statementType === 'includeNext' && termNum === maxTermToShow && isFutureTerm;
      const override = feeOverrides.find(o => o.termNumber === termNum && (o.overrideType === 'term' || o.overrideType === 'both'));
      const termDue = override && override.termFee !== undefined ? override.termFee : termFee;
      const termPaid = student.termPayments?.[`term${termNum}`] || 0;
      const attended = studentStartDate <= termEnd && today >= termStart;
      
      terms.push({ termNumber: termNum, label: `Term ${termNum}`, due: termDue, paid: termPaid, attended, isNextTerm });
      // Only add to totals if the term actually applies to this student.
      // Not-applicable terms (before the student's start date) are shown for
      // context but must not inflate the total. Future/next terms are real charges.
      const includeInTotal = attended || isNextTerm || (statementType === 'allTerms' && isFutureTerm);
      if (includeInTotal) {
        totalDue += termDue;
        totalPaid += termPaid;
      }
    }
    
    return { adminDue, adminPaid, terms, totalDue, totalPaid, totalBalance: totalDue - totalPaid };
  };

  // ============================================================
  // PDF GENERATION - Returns base64 for email attachment
  // ============================================================

  const generatePDFBytes = async (student, franchiseeData, schoolData, termStructure, statementType = 'current', includePaymentDates = false) => {
    const pdfDoc = new jsPDF();
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];
    let totalOwing = 0;
    
    const { currentTerm, nextTerm, maxTerm } = determineCurrentAndNextTerm(termStructure);
    
    // Get statement settings
    const titleSuffix = statementSettings.statementTitle || '';
    const titleBase = titleSuffix ? `BEAUX-ARTS ${titleSuffix}` : 'BEAUX-ARTS';
    const subtitle = statementSettings.statementSubtitle || '';
    
    let yPos = 25;
    let logoLoaded = false;
    
    // Add logo
    try {
      const logoImg = new Image();
      logoImg.src = '/arty.png';
      await new Promise((resolve) => {
        logoImg.onload = () => { pdfDoc.addImage(logoImg, 'PNG', 14, yPos, 25, 25); logoLoaded = true; resolve(); };
        logoImg.onerror = () => resolve();
        setTimeout(resolve, 2000);
      });
    } catch (error) { /* continue without logo */ }
    
    // Header
    pdfDoc.setFontSize(16);
    pdfDoc.setFont(undefined, 'bold');
    
    if (logoLoaded) {
      pdfDoc.text(`${titleBase} STATEMENT`, 45, yPos + 8);
      yPos += 13;
      // Subtitle if set
      if (subtitle) {
        pdfDoc.setFontSize(9);
        pdfDoc.setFont(undefined, 'italic');
        pdfDoc.text(subtitle, 45, yPos);
        yPos += 5;
      }
      // Franchisee details line
      pdfDoc.setFontSize(9);
      pdfDoc.setFont(undefined, 'normal');
      const detailParts = [];
      if (statementSettings.showFranchiseeNameOnStatement && franchiseeData.name) {
        detailParts.push(franchiseeData.name);
      }
      if (statementSettings.showFranchiseePhoneOnStatement) {
        const contactNumber = franchiseeData.whatsapp || franchiseeData.phone || '';
        if (contactNumber) detailParts.push(`Contact: ${contactNumber}`);
      }
      if (statementSettings.showFranchiseeEmailOnStatement && franchiseeData.email) {
        detailParts.push(franchiseeData.email);
      }
      if (detailParts.length > 0) {
        pdfDoc.text(detailParts.join('  |  '), 45, yPos);
      }
      yPos += 6;
      // Ensure we're past the bottom of the logo (logo is 25 units tall starting at yPos=25)
      if (yPos < 58) yPos = 58;
    } else {
      pdfDoc.text(`${titleBase} STATEMENT`, 105, yPos, { align: 'center' });
      yPos += 6;
      // Subtitle if set
      if (subtitle) {
        pdfDoc.setFontSize(9);
        pdfDoc.setFont(undefined, 'italic');
        pdfDoc.text(subtitle, 105, yPos, { align: 'center' });
        yPos += 5;
      }
      // Franchisee details line
      pdfDoc.setFontSize(9);
      pdfDoc.setFont(undefined, 'normal');
      const detailParts = [];
      if (statementSettings.showFranchiseeNameOnStatement && franchiseeData.name) {
        detailParts.push(franchiseeData.name);
      }
      if (statementSettings.showFranchiseePhoneOnStatement) {
        const contactNumber = franchiseeData.whatsapp || franchiseeData.phone || '';
        if (contactNumber) detailParts.push(`Contact: ${contactNumber}`);
      }
      if (statementSettings.showFranchiseeEmailOnStatement && franchiseeData.email) {
        detailParts.push(franchiseeData.email);
      }
      if (detailParts.length > 0) {
        pdfDoc.text(detailParts.join('  |  '), 105, yPos, { align: 'center' });
      }
      yPos += 6;
    }
    
    // Student info — uses configurable label
    pdfDoc.setFontSize(10);
    pdfDoc.text(`Date: ${todayString}`, 14, yPos);
    yPos += 5;
    pdfDoc.text(`Student: ${student.studentName}  |  School: ${schoolData.schoolName}`, 14, yPos);
    yPos += 8;
    
    // Payment Breakdown
    pdfDoc.setFontSize(10);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text("Payment Breakdown:", 14, yPos);
    yPos += 6;
    
    const studentStartDate = student.startDate?.toDate ? student.startDate.toDate() : new Date(student.startDate);
    studentStartDate.setHours(0, 0, 0, 0);
    const currentYear = today.getFullYear();
    const todayDateOnly = new Date(today);
    todayDateOnly.setHours(0, 0, 0, 0);
    const termFee = student.feeStructure?.standardFee || 0;
    const feeOverrides = student.feeOverrides || [];
    
    let maxTermToShow = 0;
    if (statementType === 'allTerms') {
      for (let termNum = 1; termNum <= maxTerm; termNum++) {
        const termData = termStructure.termDates[`term${termNum}`];
        if (!termData || !termData.start || !termData.end) continue;
        const [endMonth, endDay] = termData.end.split('-').map(Number);
        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        if (studentStartDate <= termEnd) maxTermToShow = termNum;
      }
    } else {
      for (let termNum = 1; termNum <= maxTerm; termNum++) {
        const termData = termStructure.termDates[`term${termNum}`];
        if (!termData || !termData.start || !termData.end) continue;
        const [startMonth, startDay] = termData.start.split('-').map(Number);
        const [endMonth, endDay] = termData.end.split('-').map(Number);
        const termStart = new Date(currentYear, startMonth - 1, startDay);
        termStart.setHours(0, 0, 0, 0);
        const termEnd = new Date(currentYear, endMonth - 1, endDay);
        termEnd.setHours(23, 59, 59, 999);
        const shouldCharge = (studentStartDate <= termEnd && todayDateOnly >= termStart &&
          (student.isActive || (student.deactivationInfo?.deactivatedDate && new Date(student.deactivationInfo.deactivatedDate) > termStart)));
        if (shouldCharge) maxTermToShow = termNum;
      }
      if (statementType === 'includeNext' && maxTermToShow < maxTerm) maxTermToShow += 1;
    }
    
    // Build table rows
    const breakdownRows = [];
    breakdownRows.push([
      { content: '', styles: { fontStyle: 'bold', fontSize: 9, fillColor: [248, 249, 250] } },
      { content: 'DUE', styles: { fontStyle: 'bold', fontSize: 9, halign: 'right', fillColor: [248, 249, 250] } },
      { content: 'PAID', styles: { fontStyle: 'bold', fontSize: 9, halign: 'right', fillColor: [248, 249, 250] } },
      { content: 'BALANCE', styles: { fontStyle: 'bold', fontSize: 9, halign: 'right', fillColor: [248, 249, 250] } }
    ]);
    
    // Admin fee
    const adminOverride = feeOverrides.find(o => o.overrideType === 'admin' || o.overrideType === 'both');
    const adminDue = adminOverride && adminOverride.adminFee !== undefined ? adminOverride.adminFee : (student.adminFee || 0);
    const adminPaid = student.termPayments?.admin || 0;
    const adminBalance = adminDue - adminPaid;
    
    breakdownRows.push([
      { content: 'Admin Fee', styles: { fontStyle: 'bold', fillColor: [255, 251, 240] } },
      { content: `R${adminDue.toFixed(2)}`, styles: { halign: 'right', fillColor: [255, 251, 240] } },
      { content: `R${adminPaid.toFixed(2)}`, styles: { halign: 'right', textColor: adminPaid > 0 ? [39, 174, 96] : [102, 102, 102], fillColor: [255, 251, 240] } },
      { content: `R${adminBalance.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', textColor: adminBalance > 0 ? [231, 76, 60] : [39, 174, 96], fillColor: [255, 251, 240] } }
    ]);
    
    let totalDue = adminDue;
    let totalPaidCalc = adminPaid;
    let totalBalance = adminBalance;
    
    for (let termNum = 1; termNum <= maxTermToShow; termNum++) {
      const termData = termStructure.termDates[`term${termNum}`];
      if (!termData || !termData.start || !termData.end) continue;
      const [startMonth, startDay] = termData.start.split('-').map(Number);
      const [endMonth, endDay] = termData.end.split('-').map(Number);
      const termStart = new Date(currentYear, startMonth - 1, startDay);
      termStart.setHours(0, 0, 0, 0);
      const termEnd = new Date(currentYear, endMonth - 1, endDay);
      termEnd.setHours(23, 59, 59, 999);
      const isFutureTerm = todayDateOnly < termStart;
      const isNextTerm = statementType === 'includeNext' && termNum === maxTermToShow && isFutureTerm;
      const override = feeOverrides.find(o => o.termNumber === termNum && (o.overrideType === 'term' || o.overrideType === 'both'));
      const termDueVal = override && override.termFee !== undefined ? override.termFee : termFee;
      const termPaidVal = student.termPayments?.[`term${termNum}`] || 0;
      const termBalance = termDueVal - termPaidVal;
      const attended = studentStartDate <= termEnd && todayDateOnly >= termStart;
      
      let rowStyle = {};
      let labelText = `Term ${termNum}`;
      if (isNextTerm || (statementType === 'allTerms' && isFutureTerm)) {
        rowStyle = { fillColor: [255, 255, 200] };
      } else if (!attended) {
        rowStyle = { fillColor: [242, 243, 245], textColor: [150, 150, 150] };
        labelText = `Term ${termNum} (not applicable)`;
      }
      
      breakdownRows.push([
        { content: labelText, styles: { fontStyle: 'bold', ...rowStyle } },
        { content: `R${termDueVal.toFixed(2)}`, styles: { halign: 'right', ...rowStyle } },
        { content: `R${termPaidVal.toFixed(2)}`, styles: { halign: 'right', textColor: termPaidVal > 0 ? [39, 174, 96] : [102, 102, 102], ...rowStyle } },
        { content: `R${termBalance.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', textColor: termBalance > 0 ? [231, 76, 60] : (termBalance < 0 ? [52, 152, 219] : [39, 174, 96]), ...rowStyle } }
      ]);
      // Only include this term in the totals if it actually applies to the student.
      // "Not applicable" terms (e.g. before the student's start date) are shown
      // greyed out for context but must NOT be added to the totals.
      // Future/next terms (highlighted yellow) are still genuine charges, so they count.
      const includeInTotal = attended || isNextTerm || (statementType === 'allTerms' && isFutureTerm);
      if (includeInTotal) {
        totalDue += termDueVal;
        totalPaidCalc += termPaidVal;
        totalBalance += termBalance;
      }
    }
    
    // Total row
    breakdownRows.push([
      { content: 'TOTAL', styles: { fontStyle: 'bold', fontSize: 10, fillColor: [248, 249, 250] } },
      { content: `R${totalDue.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, fillColor: [248, 249, 250] } },
      { content: `R${totalPaidCalc.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, textColor: [39, 174, 96], fillColor: [248, 249, 250] } },
      { content: `R${totalBalance.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', fontSize: 10, textColor: totalBalance > 0 ? [231, 76, 60] : [39, 174, 96], fillColor: [248, 249, 250] } }
    ]);
    
    autoTable(pdfDoc, {
      startY: yPos,
      head: [],
      body: breakdownRows,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, lineWidth: 0.3, lineColor: [0, 0, 0] },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40 }, 2: { cellWidth: 40 }, 3: { cellWidth: 40 } }
    });
    
    yPos = pdfDoc.lastAutoTable.finalY + 6;

    // ── Payments Received section (only on "Statement with Payment Dates") ──
    if (includePaymentDates) {
      // Helper: turn a stored date (Firestore Timestamp OR JS Date OR string) into a display string
      const formatPaymentDate = (d) => {
        if (!d) return '';
        const dateObj = d.toDate ? d.toDate() : new Date(d);
        if (isNaN(dateObj.getTime())) return '';
        return dateObj.toLocaleDateString('en-GB'); // dd/mm/yyyy
      };

      // Helper: describe what a payment was applied to (admin / term / credit)
      const describeAllocation = (payment) => {
        const parts = [];
        if (payment.adminAmount > 0) parts.push('Admin Fee');
        if (payment.termAmount > 0) parts.push('Term Fee');
        // Fall back to allocation targets if amounts aren't broken out
        if (parts.length === 0) {
          if (payment.allocatedTo) {
            parts.push(payment.allocatedTo === 'admin' ? 'Admin Fee'
              : payment.allocatedTo === 'credit' ? 'Credit'
              : payment.allocatedTo.replace('term', 'Term '));
          } else if (Array.isArray(payment.allocations)) {
            payment.allocations.forEach(a => {
              parts.push(a.target === 'admin' ? 'Admin Fee'
                : a.target === 'credit' ? 'Credit'
                : (a.target || '').replace('term', 'Term '));
            });
          }
        }
        return parts.length > 0 ? parts.join(', ') : '—';
      };

      const history = Array.isArray(student.paymentHistory) ? [...student.paymentHistory] : [];
      // Sort oldest first by date
      history.sort((a, b) => {
        const da = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const dbb = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return da - dbb;
      });

      // Make sure there's room; otherwise start a new page
      const pageHeight = pdfDoc.internal.pageSize.height;
      if (yPos > pageHeight - 50) { pdfDoc.addPage(); yPos = 20; }

      pdfDoc.setFontSize(10);
      pdfDoc.setFont(undefined, 'bold');
      pdfDoc.text('Payments Received:', 14, yPos);
      yPos += 6;

      if (history.length === 0) {
        pdfDoc.setFont(undefined, 'normal');
        pdfDoc.setFontSize(9);
        pdfDoc.text('No payments have been recorded yet.', 14, yPos);
        yPos += 8;
      } else {
        const paymentRows = [];
        paymentRows.push([
          { content: 'DATE', styles: { fontStyle: 'bold', fontSize: 9, fillColor: [248, 249, 250] } },
          { content: 'AMOUNT', styles: { fontStyle: 'bold', fontSize: 9, halign: 'right', fillColor: [248, 249, 250] } },
          { content: 'APPLIED TO', styles: { fontStyle: 'bold', fontSize: 9, fillColor: [248, 249, 250] } }
        ]);

        let paymentsTotal = 0;
        history.forEach(payment => {
          const rowAmount = (payment.adminAmount || 0) + (payment.termAmount || 0);
          paymentsTotal += rowAmount;
          paymentRows.push([
            { content: formatPaymentDate(payment.date) },
            { content: `R${rowAmount.toFixed(2)}`, styles: { halign: 'right' } },
            { content: describeAllocation(payment) }
          ]);
        });

        // Total row
        paymentRows.push([
          { content: 'TOTAL PAID', styles: { fontStyle: 'bold', fillColor: [248, 249, 250] } },
          { content: `R${paymentsTotal.toFixed(2)}`, styles: { halign: 'right', fontStyle: 'bold', textColor: [39, 174, 96], fillColor: [248, 249, 250] } },
          { content: '', styles: { fillColor: [248, 249, 250] } }
        ]);

        autoTable(pdfDoc, {
          startY: yPos,
          head: [],
          body: paymentRows,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2, lineWidth: 0.3, lineColor: [0, 0, 0] },
          columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 40 }, 2: { cellWidth: 90 } }
        });
        yPos = pdfDoc.lastAutoTable.finalY + 6;
      }
    }

    if (student.creditBalance && student.creditBalance > 0) {
      pdfDoc.setTextColor(52, 152, 219);
      pdfDoc.setFont(undefined, 'bold');
      pdfDoc.text(`Credit Balance: R${student.creditBalance.toFixed(2)}`, 14, yPos);
      pdfDoc.setFont(undefined, 'normal');
      yPos += 5;
    }
    pdfDoc.setTextColor(0, 0, 0);
    yPos += 6;
    
    totalOwing = totalBalance;
    
    // Update title to INVOICE if money owing
    if (totalOwing > 0) {
      pdfDoc.setPage(1);
      pdfDoc.setFontSize(16);
      pdfDoc.setFont(undefined, 'bold');
      if (logoLoaded) {
        // Clear entire header area (title + subtitle + details)
        pdfDoc.setFillColor(255, 255, 255);
        pdfDoc.rect(45, 24, 155, 32, 'F');
        pdfDoc.setTextColor(0, 0, 0);
        pdfDoc.text(`${titleBase} INVOICE`, 45, 33);
        let redrawY = 38;
        if (subtitle) {
          pdfDoc.setFontSize(9);
          pdfDoc.setFont(undefined, 'italic');
          pdfDoc.text(subtitle, 45, redrawY);
          redrawY += 5;
        }
        pdfDoc.setFontSize(9);
        pdfDoc.setFont(undefined, 'normal');
        const detailParts = [];
        if (statementSettings.showFranchiseeNameOnStatement && franchiseeData.name) detailParts.push(franchiseeData.name);
        if (statementSettings.showFranchiseePhoneOnStatement) {
          const cn = franchiseeData.whatsapp || franchiseeData.phone || '';
          if (cn) detailParts.push(`Contact: ${cn}`);
        }
        if (statementSettings.showFranchiseeEmailOnStatement && franchiseeData.email) detailParts.push(franchiseeData.email);
        if (detailParts.length > 0) pdfDoc.text(detailParts.join('  |  '), 45, redrawY);
      } else {
        // Clear entire header area for no-logo path
        pdfDoc.setFillColor(255, 255, 255);
        pdfDoc.rect(10, 22, 190, 28, 'F');
        pdfDoc.setTextColor(0, 0, 0);
        pdfDoc.setFontSize(16);
        pdfDoc.setFont(undefined, 'bold');
        pdfDoc.text(`${titleBase} INVOICE`, 105, 25, { align: 'center' });
        let redrawY = 31;
        if (subtitle) {
          pdfDoc.setFontSize(9);
          pdfDoc.setFont(undefined, 'italic');
          pdfDoc.text(subtitle, 105, redrawY, { align: 'center' });
          redrawY += 5;
        }
        pdfDoc.setFontSize(9);
        pdfDoc.setFont(undefined, 'normal');
        const detailParts2 = [];
        if (statementSettings.showFranchiseeNameOnStatement && franchiseeData.name) detailParts2.push(franchiseeData.name);
        if (statementSettings.showFranchiseePhoneOnStatement) {
          const cn2 = franchiseeData.whatsapp || franchiseeData.phone || '';
          if (cn2) detailParts2.push(`Contact: ${cn2}`);
        }
        if (statementSettings.showFranchiseeEmailOnStatement && franchiseeData.email) detailParts2.push(franchiseeData.email);
        if (detailParts2.length > 0) pdfDoc.text(detailParts2.join('  |  '), 105, redrawY, { align: 'center' });
      }
      pdfDoc.setPage(1);
      pdfDoc.setTextColor(0, 0, 0);
    }
    
    // Check for page overflow
    const pageHeight = pdfDoc.internal.pageSize.height;
    if (yPos > pageHeight - 80) { pdfDoc.addPage(); yPos = 20; }
    
    // Amount due
    pdfDoc.setFontSize(10);
    pdfDoc.setFont(undefined, 'bold');
    pdfDoc.text("Amount due:", 14, yPos);
    yPos += 6;
    pdfDoc.setFontSize(11);
    pdfDoc.text(`R${totalOwing.toFixed(2)}`, 14, yPos);
    yPos += 8;
    
    pdfDoc.setFontSize(10);
    pdfDoc.setFont(undefined, 'bold');
    if (totalOwing === 0) {
      const statusMessage = "Your account is up to date";
      const boxPadding = 4;
      const messageWidth = pdfDoc.getTextWidth(statusMessage);
      pdfDoc.setDrawColor(0);
      pdfDoc.setLineWidth(0.5);
      pdfDoc.rect(14, yPos - 2, messageWidth + (boxPadding * 2), 8);
      pdfDoc.text(statusMessage, 14 + boxPadding, yPos + 3);
    }
    yPos += 10;
    
    pdfDoc.setFontSize(8);
    pdfDoc.setFont(undefined, 'normal');
    pdfDoc.text("PLEASE NOTE ALL LESSONS MUST BE PAID FOR PRIOR TO THE LESSON.", 14, yPos);
    yPos += 8;
    
    // Custom statement note (if set)
    if (statementNote && statementNote.trim()) {
      pdfDoc.setFontSize(9);
      pdfDoc.setFont(undefined, 'normal');
      // Split note into lines that fit the page width
      const noteLines = pdfDoc.splitTextToSize(statementNote.trim(), 180);
      noteLines.forEach(line => {
        pdfDoc.text(line, 14, yPos);
        yPos += 4.5;
      });
      yPos += 3;
    }
    
    // Banking details
    if (franchiseeData.includeBankingDetails && franchiseeData.bankName) {
      pdfDoc.setFontSize(10);
      pdfDoc.setFont(undefined, 'bold');
      pdfDoc.text("BANKING DETAILS", 14, yPos);
      yPos += 5;
      pdfDoc.setFont(undefined, 'normal');
      pdfDoc.setFontSize(9);
      pdfDoc.text(`Bank: ${franchiseeData.bankName}`, 14, yPos); yPos += 4;
      pdfDoc.text(`Account Name: ${franchiseeData.accountName}`, 14, yPos); yPos += 4;
      pdfDoc.text(`Account Number: ${franchiseeData.accountNumber}`, 14, yPos); yPos += 4;
      pdfDoc.text(`Branch code: ${franchiseeData.branchCode}`, 14, yPos); yPos += 4;
      const reference = student.accountNumber || `${student.studentName}+${schoolData.schoolName}`;
      pdfDoc.text(`Reference: ${reference}`, 14, yPos); yPos += 6;
    }
    
    pdfDoc.setFont(undefined, 'normal');
    pdfDoc.setFontSize(8);
    pdfDoc.text("Thank you, the Beaux-Arts team", 14, yPos);
    
    return pdfDoc;
  };

  // ============================================================
  // GENERATE STATEMENT (PDF download)
  // ============================================================

  const generateStatement = async (student, statementType = 'current', includePaymentDates = false) => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const franchiseeDoc = await getDoc(franchiseeRef);
      if (!franchiseeDoc.exists()) throw new Error('Franchisee data not found');
      const franchiseeData = franchiseeDoc.data();
      
      const schoolDoc = await getDoc(student.schoolRef);
      if (!schoolDoc.exists()) throw new Error('School data not found');
      const schoolData = schoolDoc.data();
      
      if (!schoolData.termStructureRef) throw new Error('Term structure not configured for this school');
      const termStructureDoc = await getDoc(schoolData.termStructureRef);
      if (!termStructureDoc.exists()) throw new Error('Term structure data not found');
      const termStructure = termStructureDoc.data();
      
      if (statementType === 'includeNext') {
        const { nextTerm } = determineCurrentAndNextTerm(termStructure);
        if (!nextTerm) throw new Error('No next term available. You are in the last term of the year.');
      }
      
      const pdfDoc = await generatePDFBytes(student, franchiseeData, schoolData, termStructure, statementType, includePaymentDates);
      const namePart = includePaymentDates ? 'StatementWithDates' : 'Statement';
      const fileName = `${namePart}_${student.accountNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdfDoc.save(fileName);
      
    } catch (error) {
      console.error('Error generating statement:', error);
      throw error;
    }
  };

  // ============================================================
  // SEND SINGLE EMAIL (Updated - cover letter + PDF attachment)
  // ============================================================

  const sendInvoiceEmail = async (student, statementType = 'current', includePaymentDates = false) => {
    try {
      const user = auth.currentUser;
      
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const franchiseeDoc = await getDoc(franchiseeRef);
      if (!franchiseeDoc.exists()) throw new Error('Franchisee data not found');
      const franchiseeData = franchiseeDoc.data();
      
      const schoolDoc = await getDoc(student.schoolRef);
      if (!schoolDoc.exists()) throw new Error('School data not found');
      const schoolData = schoolDoc.data();
      
      if (!schoolData.termStructureRef) throw new Error('Term structure not configured for this school');
      const termStructureDoc = await getDoc(schoolData.termStructureRef);
      if (!termStructureDoc.exists()) throw new Error('Term structure data not found');
      const termStructure = termStructureDoc.data();
      
      // Generate invoice data
      const invoiceData = generateInvoiceData(student, termStructure, statementType);
      
      // Generate PDF as base64
      const pdfDoc = await generatePDFBytes(student, franchiseeData, schoolData, termStructure, statementType, includePaymentDates);
      const pdfBase64 = pdfDoc.output('datauristring').split(',')[1];
      
      const balance = invoiceData.totalBalance || 0;
      const docType = balance > 0 ? 'invoice' : 'statement';
      const pdfFilename = `${student.studentName}_${schoolData.schoolName}_${docType}.pdf`.replace(/\s+/g, '_');
      
      // Generate cover letter HTML
      const coverLetterHTML = EmailService.generateCoverLetterHTML(
        student, franchiseeData, schoolData, invoiceData, customMessage
      );
      
      // Send with attachment
      const result = await EmailService.sendInvoiceEmailWithAttachment(
        user.uid, student, invoiceData, coverLetterHTML, pdfBase64, pdfFilename
      );
      
      return result;
    } catch (error) {
      console.error('Error sending invoice email:', error);
      throw error;
    }
  };

  // ============================================================
  // SINGLE EMAIL HANDLER
  // ============================================================

  const handleSendEmailSingle = async () => {
    if (!selectedSchool || !selectedStudent) {
      alert('Please select a school and student');
      return;
    }

    setSending(true);
    
    try {
      const studentDocRef = doc(db, 'students', selectedStudent);
      const studentDoc = await getDoc(studentDocRef);
      if (!studentDoc.exists()) throw new Error('Student not found');
      
      const student = { id: studentDoc.id, ...studentDoc.data() };
      
      const recipientEmail = student.primaryContact?.email || student.parentEmail || student.guardianEmail;
      if (!recipientEmail) {
        alert("No email address found for this student. Please add an email in the student's primary contact.");
        setSending(false);
        return;
      }

      if (!window.confirm(`Send invoice email to ${recipientEmail}?`)) {
        setSending(false);
        return;
      }

      const result = await sendInvoiceEmail(student, statementTypeSingle, includePaymentDatesSingle);
      alert(result.message);
    } catch (error) {
      console.error('Error sending email:', error);
      alert(`Error sending email: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // ============================================================
  // BULK EMAIL BY SCHOOL - Preview, Send, Report
  // ============================================================

  const prepareBulkEmailPreview = async () => {
    if (!filterSchool) {
      alert('Please select a school');
      return;
    }
    if (students.length === 0) {
      alert('No active students found for this school');
      return;
    }

    // Categorise students
    const withEmail = [];
    const withoutEmail = [];
    const invalidEmail = [];

    for (const student of students) {
      const email = (student.primaryContact?.email || student.parentEmail || student.guardianEmail || '').trim();
      if (!email) {
        withoutEmail.push(student);
      } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        invalidEmail.push({ ...student, emailAddress: email });
      } else {
        withEmail.push({ ...student, emailAddress: email });
      }
    }

    const selectedSchoolData = schools.find(s => s.id === filterSchool);
    
    setPreviewData({
      schoolName: selectedSchoolData?.schoolName || 'Unknown',
      totalStudents: students.length,
      withEmail,
      withoutEmail,
      invalidEmail,
      statementType: statementTypeSchool
    });
    setShowPreview(true);
  };

  const executeBulkEmail = async () => {
    setShowPreview(false);
    setBulkSending(true);
    setBulkProgress({ current: 0, total: previewData.withEmail.length, currentStudent: 'Preparing...' });

    const results = [];
    const user = auth.currentUser;

    try {
      for (let i = 0; i < previewData.withEmail.length; i++) {
        const student = previewData.withEmail[i];
        setBulkProgress({ 
          current: i + 1, 
          total: previewData.withEmail.length, 
          currentStudent: student.studentName 
        });

        try {
          const result = await sendInvoiceEmail(student, previewData.statementType);
          results.push({
            studentName: student.studentName,
            email: student.emailAddress,
            success: result.success,
            message: result.message
          });
        } catch (error) {
          results.push({
            studentName: student.studentName,
            email: student.emailAddress,
            success: false,
            message: error.message
          });
        }

        // Small delay between emails to avoid SMTP rate limits
        if (i < previewData.withEmail.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // Save email log to Firestore
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      try {
        await addDoc(collection(db, 'invoiceBulkEmailLogs'), {
          franchiseeRef: doc(db, 'Franchisees', user.uid),
          franchiseeId: user.uid,
          schoolName: previewData.schoolName,
          schoolId: filterSchool,
          statementType: previewData.statementType,
          studentCount: previewData.totalStudents,
          emailsSent: successCount,
          emailsFailed: failCount,
          noEmailCount: previewData.withoutEmail.length,
          invalidEmailCount: previewData.invalidEmail.length,
          sentBy: user.email || 'Unknown',
          sentAt: new Date(),
          results: results
        });
      } catch (logError) {
        console.error('Error saving email log:', logError);
      }

      // Show delivery report
      setDeliveryReport({
        schoolName: previewData.schoolName,
        results,
        noEmailStudents: previewData.withoutEmail,
        invalidEmailStudents: previewData.invalidEmail
      });
      setShowDeliveryReport(true);

    } catch (error) {
      console.error('Error in bulk email:', error);
      alert(`Error sending bulk emails: ${error.message}`);
    } finally {
      setBulkSending(false);
      setBulkProgress({ current: 0, total: 0, currentStudent: '' });
    }
  };

  // ============================================================
  // DOWNLOAD HANDLERS (unchanged)
  // ============================================================

  const handleGenerateSingle = async () => {
    if (!selectedSchool || !selectedStudent) {
      alert('Please select a school and student');
      return;
    }
    setGenerating(true);
    try {
      const student = students.find(s => s.id === selectedStudent);
      if (!student) throw new Error('Student not found');
      await generateStatement(student, statementTypeSingle, includePaymentDatesSingle);
      alert('Statement generated and downloaded successfully!');
    } catch (error) {
      alert(`Error generating statement: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBySchool = async () => {
    if (!filterSchool) { alert('Please select a school'); return; }
    if (students.length === 0) { alert('No active students found for this school'); return; }
    const termType = statementTypeSchool === 'allTerms' ? 'for all terms' : statementTypeSchool === 'includeNext' ? 'with next term included' : 'for current terms only';
    if (!window.confirm(`Generate statements for ${students.length} student(s) from this school ${termType}?`)) return;
    setGenerating(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      for (const student of students) {
        try {
          await generateStatement(student, statementTypeSchool);
          successCount++;
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) { errorCount++; }
      }
      alert(`Generated ${successCount} statement(s) successfully!${errorCount > 0 ? `\n${errorCount} failed.` : ''}`);
    } catch (error) {
      alert('Error generating statements. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateAll = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const q = query(collection(db, 'students'), where('franchiseeRef', '==', franchiseeRef), where('isActive', '==', true));
      const snapshot = await getDocs(q);
      const allStudents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      if (allStudents.length === 0) { alert('No active students found'); return; }
      const termType = statementTypeAll === 'allTerms' ? 'for all terms' : statementTypeAll === 'includeNext' ? 'with next term included' : 'for current terms only';
      if (!window.confirm(`Generate statements for ALL ${allStudents.length} active student(s) across all schools ${termType}?`)) return;
      setGenerating(true);
      let successCount = 0;
      let errorCount = 0;
      for (const student of allStudents) {
        try { await generateStatement(student, statementTypeAll); successCount++; await new Promise(resolve => setTimeout(resolve, 300)); }
        catch (error) { errorCount++; }
      }
      alert(`Generated ${successCount} statement(s) successfully!${errorCount > 0 ? `\n${errorCount} failed.` : ''}`);
    } catch (error) {
      alert('Error generating statements. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ============================================================
  // NEXT TERM AVAILABILITY CHECK
  // ============================================================

  const [nextTermAvailable, setNextTermAvailable] = useState(true);

  const checkNextTermAvailable = async (student) => {
    try {
      if (!student.schoolRef) return false;
      const schoolDoc = await getDoc(student.schoolRef);
      if (!schoolDoc.exists()) return false;
      const schoolData = schoolDoc.data();
      if (!schoolData.termStructureRef) return false;
      const termStructureDoc = await getDoc(schoolData.termStructureRef);
      if (!termStructureDoc.exists()) return false;
      const termStructure = termStructureDoc.data();
      const { nextTerm } = determineCurrentAndNextTerm(termStructure);
      return nextTerm !== null;
    } catch (error) { return false; }
  };

  useEffect(() => {
    if (selectedStudent) {
      const student = students.find(s => s.id === selectedStudent);
      if (student) {
        checkNextTermAvailable(student).then(available => {
          setNextTermAvailable(available);
          if (!available && statementTypeSingle === 'includeNext') setStatementTypeSingle('current');
        });
      }
    }
  }, [selectedStudent, students, statementTypeSingle]);

  // ============================================================
  // INVOICE PER SCHOOL — Load email logs from Firestore
  // ============================================================

  const loadInvoiceEmailLogs = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Try BOTH query styles so old docs (saved with franchiseeRef) and new docs
      // (saved with franchiseeId string) are all picked up
      const [snapByRef, snapById] = await Promise.all([
        getDocs(query(collection(db, 'invoiceBulkEmailLogs'), where('franchiseeRef', '==', franchiseeRef))),
        getDocs(query(collection(db, 'invoiceBulkEmailLogs'), where('franchiseeId', '==', user.uid)))
      ]);

      // Merge, deduplicate by doc id
      const seenIds = new Set();
      const allDocs = [];
      for (const snap of [snapByRef, snapById]) {
        for (const logDoc of snap.docs) {
          if (!seenIds.has(logDoc.id)) {
            seenIds.add(logDoc.id);
            allDocs.push({ id: logDoc.id, ...logDoc.data() });
          }
        }
      }

      // Sort newest first
      allDocs.sort((a, b) => {
        const aTime = a.sentAt?.toDate ? a.sentAt.toDate() : new Date(a.sentAt || 0);
        const bTime = b.sentAt?.toDate ? b.sentAt.toDate() : new Date(b.sentAt || 0);
        return bTime - aTime;
      });

      const logs = {};
      for (const data of allDocs) {
        const schoolId = data.schoolId;
        if (schoolId && !logs[schoolId]) {
          const sentAt = data.sentAt?.toDate ? data.sentAt.toDate() : new Date(data.sentAt);
          const formattedDate = sentAt.toLocaleDateString('en-ZA', {
            day: '2-digit', month: 'short', year: 'numeric'
          });
          logs[schoolId] = {
            sentAt,
            formattedDate,
            termNumber: data.termNumber,
            emailsSent: data.emailsSent || 0,
            emailsFailed: data.emailsFailed || 0,
            studentCount: data.studentCount || 0,
            sentBy: data.sentBy || 'Unknown',
            results: data.results || []
          };
        }
      }
      console.log(`📬 Invoice email logs loaded: ${Object.keys(logs).length} school(s) with history`);
      setInvoiceEmailLogs(logs);
    } catch (error) {
      console.error('Error loading invoice email logs:', error);
    }
  };

  // ============================================================
  // INVOICE PER SCHOOL — Count owing vs paid up across all students
  // ============================================================

  const loadInvoiceStats = async () => {
    try {
      setInvoiceStatsLoading(true);
      const user = auth.currentUser;
      if (!user) return;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const q = query(collection(db, 'students'), where('franchiseeRef', '==', franchiseeRef));
      const snapshot = await getDocs(q);
      const schoolCache = {};
      const termStructureCache = {};
      let owing = 0;
      let paidUp = 0;
      for (const studentDoc of snapshot.docs) {
        const student = { id: studentDoc.id, ...studentDoc.data() };
        if (!student.schoolRef) continue;
        try {
          const schoolId = student.schoolRef?.id || student.schoolRef;
          if (!schoolCache[schoolId]) {
            const schoolDoc = await getDoc(student.schoolRef);
            if (!schoolDoc.exists()) continue;
            schoolCache[schoolId] = schoolDoc.data();
          }
          const schoolData = schoolCache[schoolId];
          if (!schoolData.termStructureRef) continue;
          const tsId = schoolData.termStructureRef?.id || schoolData.termStructureRef;
          if (!termStructureCache[tsId]) {
            const tsDoc = await getDoc(schoolData.termStructureRef);
            if (!tsDoc.exists()) continue;
            termStructureCache[tsId] = tsDoc.data();
          }
          const termStructure = termStructureCache[tsId];
          const invData = generateInvoiceData(student, termStructure, 'current');
          if (invData.totalBalance > 0) {
            owing++;
          } else if (student.isActive) {
            paidUp++;
          }
        } catch (err) {
          console.error('Error in stats for:', student.studentName, err);
        }
      }
      setInvoiceStats({ owing, paidUp });
    } catch (error) {
      console.error('Error loading invoice stats:', error);
    } finally {
      setInvoiceStatsLoading(false);
    }
  };

  // ============================================================
  // INVOICE PER SCHOOL — Load students: active + inactive who owe
  // ============================================================

  const loadInvoiceSchoolStudents = async (schoolId) => {
    try {
      setInvoiceSchoolLoading(true);
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const schoolRef = doc(db, 'schools', schoolId);
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('schoolRef', '==', schoolRef)
      );
      const snapshot = await getDocs(q);
      const schoolDoc = await getDoc(schoolRef);
      if (!schoolDoc.exists()) return;
      const schoolData = schoolDoc.data();
      let termStructure = null;
      if (schoolData.termStructureRef) {
        const tsDoc = await getDoc(schoolData.termStructureRef);
        if (tsDoc.exists()) termStructure = tsDoc.data();
      }
      const studentsList = [];
      for (const studentDoc of snapshot.docs) {
        const student = { id: studentDoc.id, ...studentDoc.data() };
        if (student.isActive) {
          studentsList.push(student);
        } else if (termStructure) {
          try {
            const invData = generateInvoiceData(student, termStructure, 'current');
            if (invData.totalBalance > 0) {
              studentsList.push({ ...student, _isInactiveOwing: true });
            }
          } catch (err) {
            console.error('Error checking inactive student:', student.studentName, err);
          }
        }
      }
      studentsList.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
      setInvoiceSchoolStudents(studentsList);
    } catch (error) {
      console.error('Error loading invoice school students:', error);
    } finally {
      setInvoiceSchoolLoading(false);
    }
  };

  // ============================================================
  // INVOICE PER SCHOOL — Download ZIP
  // ============================================================

  const handleDownloadInvoiceZip = async () => {
    if (!invoiceSchool || invoiceSchoolStudents.length === 0) return;
    const selectedSchoolObj = schools.find(s => s.id === invoiceSchool);
    const schoolName = selectedSchoolObj?.schoolName || 'School';
    if (!window.confirm(`Download invoices for ${invoiceSchoolStudents.length} student(s) from ${schoolName} as a ZIP file?`)) return;
    setIsZipping(true);
    setInvoiceProgress({ current: 0, total: invoiceSchoolStudents.length, currentStudent: 'Preparing...' });
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const franchiseeDoc = await getDoc(franchiseeRef);
      if (!franchiseeDoc.exists()) throw new Error('Franchisee data not found');
      const franchiseeData = franchiseeDoc.data();
      const schoolRef = doc(db, 'schools', invoiceSchool);
      const schoolDoc = await getDoc(schoolRef);
      if (!schoolDoc.exists()) throw new Error('School data not found');
      const schoolData = schoolDoc.data();
      if (!schoolData.termStructureRef) throw new Error('Term structure not configured for this school');
      const tsDoc = await getDoc(schoolData.termStructureRef);
      if (!tsDoc.exists()) throw new Error('Term structure not found');
      const termStructure = tsDoc.data();
      // Dynamic import of JSZip
      let JSZip;
      try {
        JSZip = (await import('jszip')).default;
      } catch {
        throw new Error('JSZip not installed. Run: npm install jszip');
      }
      const zip = new JSZip();
      let successCount = 0;
      for (let i = 0; i < invoiceSchoolStudents.length; i++) {
        const student = invoiceSchoolStudents[i];
        setInvoiceProgress({ current: i + 1, total: invoiceSchoolStudents.length, currentStudent: student.studentName });
        try {
          const pdfDoc = await generatePDFBytes(student, franchiseeData, schoolData, termStructure, 'current');
          const pdfBytes = pdfDoc.output('arraybuffer');
          const invData = generateInvoiceData(student, termStructure, 'current');
          const docType = invData.totalBalance > 0 ? 'Invoice' : 'Statement';
          const safeName = `${student.studentName}_${docType}_Term${invoiceTerm}.pdf`.replace(/[^a-zA-Z0-9_.-]/g, '_');
          zip.file(safeName, pdfBytes);
          successCount++;
        } catch (err) {
          console.error('Error generating PDF for', student.studentName, err);
        }
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${schoolName}_Invoices_Term${invoiceTerm}_${new Date().toISOString().split('T')[0]}.zip`.replace(/\s+/g, '_');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert(`Downloaded ${successCount} invoice(s) as ZIP successfully!`);
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert(`Error creating ZIP: ${error.message}`);
    } finally {
      setIsZipping(false);
      setInvoiceProgress({ current: 0, total: 0, currentStudent: '' });
    }
  };

  // ============================================================
  // INVOICE PER SCHOOL — Show preview before emailing
  // ============================================================

  const prepareInvoiceEmailPreview = () => {
    if (!invoiceSchool || invoiceSchoolStudents.length === 0) return;
    const selectedSchoolObj = schools.find(s => s.id === invoiceSchool);
    const withEmail = [];
    const withoutEmail = [];
    const invalidEmail = [];
    for (const student of invoiceSchoolStudents) {
      const email = (student.primaryContact?.email || student.parentEmail || student.guardianEmail || '').trim();
      if (!email) {
        withoutEmail.push(student);
      } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        invalidEmail.push({ ...student, emailAddress: email });
      } else {
        withEmail.push({ ...student, emailAddress: email });
      }
    }
    setInvoicePreviewData({
      schoolName: selectedSchoolObj?.schoolName || 'Unknown',
      schoolId: invoiceSchool,
      termNumber: invoiceTerm,
      totalStudents: invoiceSchoolStudents.length,
      withEmail,
      withoutEmail,
      invalidEmail
    });
    setShowInvoicePreview(true);
  };

  // ============================================================
  // INVOICE PER SCHOOL — Send emails + save log
  // ============================================================

  const executeInvoiceBulkEmail = async () => {
    setShowInvoicePreview(false);
    setIsBulkInvoicing(true);
    setInvoiceProgress({ current: 0, total: invoicePreviewData.withEmail.length, currentStudent: 'Preparing...' });
    const results = [];
    const user = auth.currentUser;
    try {
      for (let i = 0; i < invoicePreviewData.withEmail.length; i++) {
        const student = invoicePreviewData.withEmail[i];
        setInvoiceProgress({
          current: i + 1,
          total: invoicePreviewData.withEmail.length,
          currentStudent: student.studentName
        });
        try {
          const result = await sendInvoiceEmail(student, 'current');
          results.push({ studentName: student.studentName, email: student.emailAddress, success: result.success, message: result.message });
        } catch (error) {
          results.push({ studentName: student.studentName, email: student.emailAddress, success: false, message: error.message });
        }
        if (i < invoicePreviewData.withEmail.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      // Save log to Firestore
      try {
        await addDoc(collection(db, 'invoiceBulkEmailLogs'), {
          franchiseeRef: doc(db, 'Franchisees', user.uid),
          franchiseeId: user.uid,
          schoolName: invoicePreviewData.schoolName,
          schoolId: invoicePreviewData.schoolId,
          termNumber: invoicePreviewData.termNumber,
          studentCount: invoicePreviewData.totalStudents,
          emailsSent: successCount,
          emailsFailed: failCount,
          noEmailCount: invoicePreviewData.withoutEmail.length,
          sentBy: user.email || 'Unknown',
          sentAt: new Date(),
          results: results
        });
        await loadInvoiceEmailLogs(); // Refresh so ✅ ticks update
      } catch (logError) {
        console.error('Error saving invoice email log:', logError);
      }
      setInvoiceReport({
        schoolName: invoicePreviewData.schoolName,
        termNumber: invoicePreviewData.termNumber,
        results,
        noEmailStudents: invoicePreviewData.withoutEmail,
        invalidEmailStudents: invoicePreviewData.invalidEmail
      });
      setShowInvoiceReport(true);
    } catch (error) {
      console.error('Error in invoice bulk email:', error);
      alert(`Error sending invoice emails: ${error.message}`);
    } finally {
      setIsBulkInvoicing(false);
      setInvoiceProgress({ current: 0, total: 0, currentStudent: '' });
    }
  };

  // ============================================================
  // INVOICE PER SCHOOL — Preview Modal
  // ============================================================

  const renderInvoicePreviewModal = () => {
    if (!showInvoicePreview || !invoicePreviewData) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowInvoicePreview(false)}>
        <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
          <div className="preview-header">
            <h2>📧 Invoice Preview — {invoicePreviewData.schoolName} · Term {invoicePreviewData.termNumber}</h2>
            <button onClick={() => setShowInvoicePreview(false)} className="close-btn">✕</button>
          </div>
          <div className="preview-body">
            <div className="preview-stats">
              <div className="stat-item stat-total">
                <span className="stat-number">{invoicePreviewData.totalStudents}</span>
                <span className="stat-label">Total Students</span>
              </div>
              <div className="stat-item stat-email">
                <span className="stat-number">{invoicePreviewData.withEmail.length}</span>
                <span className="stat-label">Will Receive Email</span>
              </div>
              {invoicePreviewData.withoutEmail.length > 0 && (
                <div className="stat-item stat-noemail">
                  <span className="stat-number">{invoicePreviewData.withoutEmail.length}</span>
                  <span className="stat-label">No Email</span>
                </div>
              )}
              {invoicePreviewData.invalidEmail.length > 0 && (
                <div className="stat-item stat-invalid">
                  <span className="stat-number">{invoicePreviewData.invalidEmail.length}</span>
                  <span className="stat-label">Invalid Email</span>
                </div>
              )}
            </div>
            <div className="preview-section">
              <div className="preview-info-note">
                <strong>ℹ️ What will be sent:</strong> Students with an outstanding balance receive an <strong>INVOICE</strong>; fully paid-up students receive a <strong>STATEMENT</strong>. All arrears from previous terms are included on the invoice.
              </div>
            </div>
            <div className="preview-section">
              <h3>✉️ Recipients ({invoicePreviewData.withEmail.length})</h3>
              <div className="preview-recipients-list">
                {invoicePreviewData.withEmail.map((s, i) => (
                  <div key={i} className="recipient-row">
                    <span className="recipient-name">
                      {s.studentName}
                      {s._isInactiveOwing && <span className="inactive-badge"> · inactive — still owing</span>}
                    </span>
                    <span className="recipient-email">{s.emailAddress}</span>
                  </div>
                ))}
              </div>
            </div>
            {invoicePreviewData.withoutEmail.length > 0 && (
              <div className="preview-section">
                <h3>⚠️ No Email Address — Will Not Receive ({invoicePreviewData.withoutEmail.length})</h3>
                <div className="preview-warning-list">
                  {invoicePreviewData.withoutEmail.map((s, i) => (
                    <div key={i} className="warning-row">
                      • {s.studentName}
                      {s._isInactiveOwing && <span className="inactive-badge"> · inactive — still owing</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {invoicePreviewData.invalidEmail.length > 0 && (
              <div className="preview-section">
                <h3>❌ Invalid Email Format ({invoicePreviewData.invalidEmail.length})</h3>
                <div className="preview-error-list">
                  {invoicePreviewData.invalidEmail.map((s, i) => (
                    <div key={i} className="error-row">• {s.studentName}: {s.emailAddress}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="preview-footer">
            <button onClick={() => setShowInvoicePreview(false)} className="btn-cancel">Cancel</button>
            {invoicePreviewData.withEmail.length > 0 && (
              <button onClick={executeInvoiceBulkEmail} className="btn-send-bulk">
                📧 Send {invoicePreviewData.withEmail.length} Invoice{invoicePreviewData.withEmail.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // INVOICE PER SCHOOL — Delivery Report Modal
  // ============================================================

  const renderInvoiceReport = () => {
    if (!showInvoiceReport || !invoiceReport) return null;
    const successful = invoiceReport.results.filter(r => r.success);
    const failed = invoiceReport.results.filter(r => !r.success);
    return (
      <div className="modal-overlay" onClick={() => setShowInvoiceReport(false)}>
        <div className="delivery-report-modal" onClick={(e) => e.stopPropagation()}>
          <div className="report-header">
            <h2>
              {failed.length === 0 ? '✅' : '⚠️'} Delivery Report — {invoiceReport.schoolName} · Term {invoiceReport.termNumber}
            </h2>
            <button onClick={() => setShowInvoiceReport(false)} className="close-btn">✕</button>
          </div>
          <div className="report-body">
            <div className="report-stats">
              <div className="report-stat stat-success">
                <span className="stat-number">{successful.length}</span>
                <span className="stat-label">Sent</span>
              </div>
              <div className="report-stat stat-fail">
                <span className="stat-number">{failed.length}</span>
                <span className="stat-label">Failed</span>
              </div>
              {invoiceReport.noEmailStudents?.length > 0 && (
                <div className="report-stat stat-noemail">
                  <span className="stat-number">{invoiceReport.noEmailStudents.length}</span>
                  <span className="stat-label">No Email</span>
                </div>
              )}
            </div>
            {failed.length > 0 && (
              <div className="report-section">
                <h3 className="section-title-fail">❌ Failed — Action Required</h3>
                <div className="report-list fail-list">
                  {failed.map((r, i) => (
                    <div key={i} className="report-list-item-fail">
                      <div className="item-header">
                        <span className="item-icon">✗</span>
                        <span className="item-name">{r.studentName}</span>
                        <span className="item-email">{r.email}</span>
                      </div>
                      <div className="item-reason">{r.message}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {successful.length > 0 && (
              <div className="report-section">
                <h3 className="section-title-success">✅ Successfully Sent ({successful.length})</h3>
                <div className="report-list success-list">
                  {successful.map((r, i) => (
                    <div key={i} className="report-list-item">
                      <span className="item-icon">✓</span>
                      <span className="item-name">{r.studentName}</span>
                      <span className="item-email">{r.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {invoiceReport.noEmailStudents?.length > 0 && (
              <div className="report-section">
                <h3 className="section-title-warning">⚠️ No Email Address — Could Not Send</h3>
                <div className="report-list warning-list">
                  {invoiceReport.noEmailStudents.map((s, i) => (
                    <div key={i} className="report-list-item">
                      <span className="item-icon">—</span>
                      <span className="item-name">{s.studentName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="report-info-note">
              <strong>ℹ️ Note:</strong> "Sent" means the email was accepted by the mail server. Check your email for bounce notifications over the next 24–48 hours.
            </div>
          </div>
          <div className="report-footer">
            <button onClick={() => setShowInvoiceReport(false)} className="btn-close-report">Done</button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER - Preview Modal
  // ============================================================

  const renderPreviewModal = () => {
    if (!showPreview || !previewData) return null;
    
    return (
      <div className="modal-overlay" onClick={() => setShowPreview(false)}>
        <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
          <div className="preview-header">
            <h2>📧 Email Preview - {previewData.schoolName}</h2>
            <button onClick={() => setShowPreview(false)} className="close-btn">✕</button>
          </div>
          
          <div className="preview-body">
            {/* Summary stats */}
            <div className="preview-stats">
              <div className="stat-item stat-total">
                <span className="stat-number">{previewData.totalStudents}</span>
                <span className="stat-label">Total Students</span>
              </div>
              <div className="stat-item stat-email">
                <span className="stat-number">{previewData.withEmail.length}</span>
                <span className="stat-label">Will Receive Email</span>
              </div>
              {previewData.withoutEmail.length > 0 && (
                <div className="stat-item stat-noemail">
                  <span className="stat-number">{previewData.withoutEmail.length}</span>
                  <span className="stat-label">No Email Address</span>
                </div>
              )}
              {previewData.invalidEmail.length > 0 && (
                <div className="stat-item stat-invalid">
                  <span className="stat-number">{previewData.invalidEmail.length}</span>
                  <span className="stat-label">Invalid Email</span>
                </div>
              )}
            </div>

            {/* Custom message preview */}
            <div className="preview-section">
              <h3>📝 Email Message</h3>
              <div className="preview-message-box">
                <p><strong>Dear Parent/Guardian,</strong></p>
                <p>{customMessage || 'Please find attached your invoice/statement for [student] at [school].'}</p>
                <p style={{ fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                  + Amount summary + Banking details + PDF attachment
                </p>
              </div>
            </div>

            {/* Recipients list */}
            <div className="preview-section">
              <h3>✉️ Recipients ({previewData.withEmail.length})</h3>
              <div className="preview-recipients-list">
                {previewData.withEmail.map((s, i) => (
                  <div key={i} className="recipient-row">
                    <span className="recipient-name">{s.studentName}</span>
                    <span className="recipient-email">{s.emailAddress}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Students without email */}
            {previewData.withoutEmail.length > 0 && (
              <div className="preview-section">
                <h3>⚠️ No Email Address ({previewData.withoutEmail.length})</h3>
                <div className="preview-warning-list">
                  {previewData.withoutEmail.map((s, i) => (
                    <div key={i} className="warning-row">• {s.studentName}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Invalid emails */}
            {previewData.invalidEmail.length > 0 && (
              <div className="preview-section">
                <h3>❌ Invalid Email Format ({previewData.invalidEmail.length})</h3>
                <div className="preview-error-list">
                  {previewData.invalidEmail.map((s, i) => (
                    <div key={i} className="error-row">• {s.studentName}: {s.emailAddress}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Info note */}
            <div className="preview-info-note">
              <strong>Note:</strong> Each student will receive an HTML email with their personal PDF invoice/statement attached. 
              Emails are sent one at a time with a short delay to avoid spam filters.
            </div>
          </div>
          
          <div className="preview-footer">
            <button onClick={() => setShowPreview(false)} className="btn-cancel">Cancel</button>
            {previewData.withEmail.length > 0 && (
              <button onClick={executeBulkEmail} className="btn-send-bulk">
                📧 Send {previewData.withEmail.length} Email{previewData.withEmail.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER - Delivery Report Modal
  // ============================================================

  const renderDeliveryReport = () => {
    if (!showDeliveryReport || !deliveryReport) return null;
    
    const successful = deliveryReport.results.filter(r => r.success);
    const failed = deliveryReport.results.filter(r => !r.success);
    
    return (
      <div className="modal-overlay" onClick={() => setShowDeliveryReport(false)}>
        <div className="delivery-report-modal" onClick={(e) => e.stopPropagation()}>
          <div className="report-header">
            <h2>
              {failed.length === 0 ? '✅' : '⚠️'} Delivery Report - {deliveryReport.schoolName}
            </h2>
            <button onClick={() => setShowDeliveryReport(false)} className="close-btn">✕</button>
          </div>
          
          <div className="report-body">
            {/* Stats row */}
            <div className="report-stats">
              <div className="report-stat stat-success">
                <span className="stat-number">{successful.length}</span>
                <span className="stat-label">Sent</span>
              </div>
              <div className="report-stat stat-fail">
                <span className="stat-number">{failed.length}</span>
                <span className="stat-label">Failed</span>
              </div>
              <div className="report-stat stat-noemail">
                <span className="stat-number">{deliveryReport.noEmailStudents.length}</span>
                <span className="stat-label">No Email</span>
              </div>
              {deliveryReport.invalidEmailStudents.length > 0 && (
                <div className="report-stat stat-invalid">
                  <span className="stat-number">{deliveryReport.invalidEmailStudents.length}</span>
                  <span className="stat-label">Invalid</span>
                </div>
              )}
            </div>

            {/* Successfully sent */}
            {successful.length > 0 && (
              <div className="report-section">
                <h3 className="section-title-success">✅ Successfully Sent</h3>
                <div className="report-list success-list">
                  {successful.map((r, i) => (
                    <div key={i} className="report-list-item">
                      <span className="item-icon">✓</span>
                      <span className="item-name">{r.studentName}</span>
                      <span className="item-email">{r.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed */}
            {failed.length > 0 && (
              <div className="report-section">
                <h3 className="section-title-fail">❌ Failed to Send</h3>
                <div className="report-list fail-list">
                  {failed.map((r, i) => (
                    <div key={i} className="report-list-item-fail">
                      <div className="item-header">
                        <span className="item-icon">✗</span>
                        <span className="item-name">{r.studentName}</span>
                        <span className="item-email">{r.email}</span>
                      </div>
                      <div className="item-reason">{r.message}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No email */}
            {deliveryReport.noEmailStudents.length > 0 && (
              <div className="report-section">
                <h3 className="section-title-warning">⚠️ No Email Address</h3>
                <div className="report-list warning-list">
                  {deliveryReport.noEmailStudents.map((s, i) => (
                    <div key={i} className="report-list-item">
                      <span className="item-icon">—</span>
                      <span className="item-name">{s.studentName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info note */}
            <div className="report-info-note">
              <strong>ℹ️ Note:</strong> "Sent" means the email was accepted by the mail server, not necessarily delivered to the inbox. 
              Check your email account for bounce notifications over the next 24-48 hours. 
              You can view email history in Reports → Email Delivery Log.
            </div>
          </div>
          
          <div className="report-footer">
            <button onClick={() => setShowDeliveryReport(false)} className="btn-close-report">Close</button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER - Progress Bar
  // ============================================================

  const renderBulkProgress = () => {
    if (!bulkSending) return null;
    
    const percent = bulkProgress.total > 0 ? Math.round((bulkProgress.current / bulkProgress.total) * 100) : 0;
    
    return (
      <div className="bulk-progress-section">
        <div className="progress-bar-container">
          <div className="progress-bar-fill" style={{ width: `${percent}%` }}></div>
        </div>
        <div className="progress-text">
          📧 Sending to {bulkProgress.currentStudent}... ({bulkProgress.current} of {bulkProgress.total})
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER - Statement Type Radio Buttons
  // ============================================================

  const renderStatementTypeRadios = (name, value, onChange, disabled = false, showNextTermNote = false) => (
    <div className="form-group">
      <label>Statement Type *</label>
      <div className="radio-group">
        <label className="radio-label">
          <input type="radio" name={name} checked={value === 'current'} onChange={() => onChange('current')} disabled={disabled} />
          <span>Current terms only</span>
        </label>
        <label className="radio-label">
          <input type="radio" name={name} checked={value === 'includeNext'} onChange={() => onChange('includeNext')} 
            disabled={disabled || (name === 'singleTermType' && !nextTermAvailable)} />
          <span>Include next term {name === 'singleTermType' && !nextTermAvailable && selectedStudent && '(Not available - last term)'}</span>
        </label>
        <label className="radio-label">
          <input type="radio" name={name} checked={value === 'allTerms'} onChange={() => onChange('allTerms')} disabled={disabled} />
          <span>All terms for year</span>
        </label>
      </div>
      {showNextTermNote && value === 'includeNext' && (
        <p className="info-note">ℹ️ Students in the last term will receive current terms only</p>
      )}
      {value === 'allTerms' && (
        <p className="info-note">ℹ️ All terms from each student's start date will be included</p>
      )}
    </div>
  );

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading) {
    return (
      <div className="statement-generation-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="statement-generation-container">
      <div className="statement-generation-header">
        <h1>📄 Generate Statements</h1>
        <p className="subtitle">Create, download and email student fee statements</p>
      </div>

      {/* Custom Message Section */}
      <div className="statement-generation-content">
        <div className="generation-section message-section">
          <div className="section-header">
            <h2>📝 Email & Statement Customisation</h2>
            <p>Customise the email message and add notes to your invoices/statements</p>
          </div>
          
          <div className="message-fields-row">
            <div className="form-group message-field">
              <label>📧 Email Message to Parents</label>
              <p className="field-description">This message appears in the email body when sending invoices. Leave blank for a default message.</p>
              <textarea
                className="custom-message-textarea"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="E.g. Thank you for your ongoing support. Please find attached your invoice for the current term. If you have any questions, please don't hesitate to contact us."
                rows={4}
              />
              <div className="message-save-indicator">
                {savingMessage ? '💾 Saving...' : customMessageLoaded ? '✓ Auto-saved' : ''}
              </div>
            </div>

            <div className="form-group message-field">
              <label>📄 Note on Invoice / Statement</label>
              <p className="field-description">This note appears on the PDF itself (downloaded & emailed). Change it anytime — e.g. "Eid Mubarak", "Wear your pottery T-shirts", etc.</p>
              <textarea
                className="custom-message-textarea"
                value={statementNote}
                onChange={(e) => setStatementNote(e.target.value)}
                placeholder='E.g. "Please note that no pottery will be handed out on outstanding accounts" or "Happy Easter from the Beaux-Arts team!"'
                rows={4}
              />
              <div className="message-save-indicator">
                {savingNote ? '💾 Saving...' : customMessageLoaded ? '✓ Auto-saved' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Single Statement Section */}
        <div className="generation-section">
          <div className="section-header">
            <h2>📄 Single Student</h2>
            <p>Generate or email a statement for one student</p>
          </div>

          <div className="form-group">
            <label>Select School *</label>
            <select value={selectedSchool} onChange={(e) => { setSelectedSchool(e.target.value); setSelectedStudent(''); }} className="form-select">
              <option value="">Choose a school...</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>{school.schoolName}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Select Student *</label>
            <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className="form-select" disabled={!selectedSchool}>
              <option value="">{selectedSchool ? 'Select a student...' : 'Select a school first'}</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>{student.studentName} ({student.accountNumber})</option>
              ))}
            </select>
          </div>

          {renderStatementTypeRadios('singleTermType', statementTypeSingle, setStatementTypeSingle, !selectedStudent)}

          <div className="form-group" style={{ marginTop: '4px' }}>
            <label className="radio-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: selectedStudent ? 'pointer' : 'not-allowed' }}>
              <input
                type="checkbox"
                checked={includePaymentDatesSingle}
                onChange={(e) => setIncludePaymentDatesSingle(e.target.checked)}
                disabled={!selectedStudent}
              />
              <span>📅 Statement with payment dates</span>
            </label>
            <p className="info-note">ℹ️ Adds a "Payments Received" section listing each payment with its date, amount and what it paid. Leave unticked for the standard statement.</p>
          </div>

          <div className="button-group">
            <button onClick={handleGenerateSingle} disabled={!selectedSchool || !selectedStudent || generating} className="btn-generate btn-single">
              {generating ? '⏳ Generating...' : (includePaymentDatesSingle ? '⬇ Download Statement with Dates' : '⬇ Download PDF')}
            </button>
            <button onClick={handleSendEmailSingle} disabled={!selectedSchool || !selectedStudent || sending} className="btn-email">
              {sending ? '📧 Sending...' : '📧 Send Email'}
            </button>
          </div>
        </div>

        {/* Bulk by School Section - UPDATED with Email button */}
        <div className="generation-section">
          <div className="section-header">
            <h2>🏫 By School (Bulk)</h2>
            <p>Generate or email statements for all students in a school</p>
          </div>

          <div className="form-group">
            <label>Select School *</label>
            <select value={filterSchool} onChange={(e) => setFilterSchool(e.target.value)} className="form-select">
              <option value="">Choose a school...</option>
              {schools.map(school => (
                <option key={school.id} value={school.id}>{school.schoolName}</option>
              ))}
            </select>
          </div>

          {filterSchool && (
            <div className="student-count-info">
              📊 {students.length} active student(s) at this school
            </div>
          )}

          {renderStatementTypeRadios('schoolTermType', statementTypeSchool, setStatementTypeSchool, !filterSchool, true)}

          {/* Bulk progress bar */}
          {renderBulkProgress()}

          <div className="button-group">
            <button onClick={handleGenerateBySchool} disabled={!filterSchool || students.length === 0 || generating || bulkSending} className="btn-generate btn-school">
              {generating ? '⏳ Generating...' : '⬇ Download All PDFs'}
            </button>
            <button onClick={prepareBulkEmailPreview} disabled={!filterSchool || students.length === 0 || bulkSending || generating} className="btn-email-bulk">
              {bulkSending ? '📧 Sending...' : '📧 Email All for School'}
            </button>
          </div>
        </div>

        {/* ── Invoice Emails Per School ── */}
        <div className="generation-section invoice-per-school-section">
          <div className="section-header">
            <h2>📧 Invoice Emails Per School</h2>
            <p>Email term invoices to all students in a school — includes inactive students who still owe</p>
          </div>

          {/* Term Selector */}
          <div className="invoice-term-selector">
            <label className="invoice-term-label">Select which term you are invoicing for:</label>
            <div className="invoice-term-buttons">
              {[1, 2, 3, 4].map(t => (
                <button
                  key={t}
                  className={`invoice-term-btn ${invoiceTerm === t ? 'invoice-term-btn-active' : ''}`}
                  onClick={() => setInvoiceTerm(t)}
                >
                  Term {t}
                </button>
              ))}
            </div>
          </div>

          {/* Stats: Owing vs Paid Up */}
          <div className="invoice-stats-row">
            <div className="invoice-stat-card invoice-stat-owing">
              <span className="invoice-stat-number">
                {invoiceStatsLoading ? '...' : invoiceStats.owing}
              </span>
              <span className="invoice-stat-label">Owing</span>
            </div>
            <div className="invoice-stat-card invoice-stat-paidup">
              <span className="invoice-stat-number">
                {invoiceStatsLoading ? '...' : invoiceStats.paidUp}
              </span>
              <span className="invoice-stat-label">Paid Up</span>
            </div>
          </div>

          {/* School Dropdown — ✅ shows if previously emailed */}
          <div className="form-group">
            <label>Select School *</label>
            <select
              value={invoiceSchool}
              onChange={(e) => setInvoiceSchool(e.target.value)}
              className="form-select"
            >
              <option value="">Select a school...</option>
              {schools.map(school => {
                const log = invoiceEmailLogs[school.id];
                const label = log
                  ? `✅ ${school.schoolName} · emailed ${log.formattedDate}`
                  : school.schoolName;
                return (
                  <option key={school.id} value={school.id}>{label}</option>
                );
              })}
            </select>
          </div>

          {/* Email log banner for selected school */}
          {invoiceSchool && invoiceEmailLogs[invoiceSchool] && (() => {
            const log = invoiceEmailLogs[invoiceSchool];
            return (
              <div className="invoice-email-log-banner">
                <span className="log-banner-icon">📬</span>
                <div className="log-banner-details">
                  <span className="log-banner-main">
                    Last emailed: <strong>Term {log.termNumber}</strong> on <strong>{log.formattedDate}</strong> · <strong>{log.emailsSent}</strong> sent successfully
                  </span>
                  {log.emailsFailed > 0 && (
                    <span className="log-banner-failed">
                      ⚠️ {log.emailsFailed} failed to send — correct the email address and re-send individually
                    </span>
                  )}
                  <span className="log-banner-bounce-tip">
                    📨 Bounced emails arrive back in your outgoing email inbox — check it after sending
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Student count for selected school */}
          {invoiceSchool && (
            <div className="student-count-info">
              {invoiceSchoolLoading
                ? '⏳ Loading students...'
                : `📊 ${invoiceSchoolStudents.length} student(s) to invoice`
              }
              {!invoiceSchoolLoading && invoiceSchoolStudents.filter(s => s._isInactiveOwing).length > 0 && (
                <span className="inactive-owing-tag">
                  &nbsp;· {invoiceSchoolStudents.filter(s => s._isInactiveOwing).length} inactive but still owing
                </span>
              )}
            </div>
          )}

          {/* Progress bar (ZIP or emailing) */}
          {(isZipping || isBulkInvoicing) && invoiceProgress.total > 0 && (
            <div className="bulk-progress-section">
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${Math.round((invoiceProgress.current / invoiceProgress.total) * 100)}%` }}
                />
              </div>
              <div className="progress-text">
                {isBulkInvoicing ? '📧 Emailing:' : '📦 Generating:'} {invoiceProgress.currentStudent} ({invoiceProgress.current} of {invoiceProgress.total})
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="button-group">
            <button
              onClick={handleDownloadInvoiceZip}
              disabled={!invoiceSchool || invoiceSchoolStudents.length === 0 || isZipping || isBulkInvoicing || invoiceSchoolLoading}
              className="btn-generate btn-school"
            >
              {isZipping ? '⏳ Creating ZIP...' : '⬇ Download ZIP'}
            </button>
            <button
              onClick={prepareInvoiceEmailPreview}
              disabled={!invoiceSchool || invoiceSchoolStudents.length === 0 || isBulkInvoicing || isZipping || invoiceSchoolLoading}
              className="btn-email-bulk"
            >
              {isBulkInvoicing ? '📧 Sending...' : '📧 Email Invoices'}
            </button>
          </div>

          {/* How it works info box */}
          <div className="info-box">
            <div className="info-icon">ℹ️</div>
            <div className="info-text">
              <strong>How it works</strong>
              <p>• Students owing money receive an INVOICE — paid-up students receive a STATEMENT</p>
              <p>• All arrears are included — if a student owes from Term 1 and you're invoicing Term 2, both appear on the invoice</p>
              <p>• Inactive students with an outstanding balance are included automatically</p>
              <p>• Download — creates a single ZIP file with all PDFs for the selected school</p>
              <p>• Email — sends individual PDFs to each student with a valid email address</p>
              <p>• Schools with ✅ in the dropdown have been emailed before — check the log before re-sending</p>
              <p>• Bounced emails will arrive back in your outgoing email inbox — check it after sending</p>
            </div>
          </div>
        </div>

        {/* All Schools Section */}
        <div className="generation-section">
          <div className="section-header">
            <h2>⬇ Download All Statements</h2>
            <p>Generate statements for all active students across all schools</p>
          </div>

          <div className="info-box">
            <div className="info-icon">⚠️</div>
            <div className="info-text">
              <strong>Bulk Generation</strong>
              <p>This will generate statements for every active student in your franchise. This may take a few minutes depending on the number of students.</p>
            </div>
          </div>

          {renderStatementTypeRadios('allTermType', statementTypeAll, setStatementTypeAll)}

          <button onClick={handleGenerateAll} disabled={generating || schools.length === 0} className="btn-generate btn-all">
            {generating ? '⏳ Generating...' : '⬇ Download All Statements'}
          </button>
        </div>
      </div>

      {/* Modals */}
      {renderPreviewModal()}
      {renderDeliveryReport()}
      {renderInvoicePreviewModal()}
      {renderInvoiceReport()}
    </div>
  );
}

export default StatementGeneration;